import * as React from "react";
import {BaseFormViewState, default as BaseFormView} from "../../../../ui/form/BaseFormView";
import FormButtonsView from "../../../../ui/form/FormButtonsView";
import ButtonPrimary from "../../../../../hiberUiKit/src/atoms/Buttons/ButtonPrimary";
import ButtonTertiary from "../../../../../hiberUiKit/src/atoms/Buttons/ButtonTertiary";
import SectionView from "../../../../layout/section/SectionView";
import Address from "../../../../../model/address/Address";
import {none, option, Option} from "ts-option";
import PartnerBrandFormBody from "../../PartnerBrandFormBody";
import PartnerBrand from "../../../../../model/partnerBrand/PartnerBrand";
import PartnerBrandForm from "../../../../partnerBrand/PartnerBrandForm";
import PartnerBrandPoints from "../../../../../model/partnerBrand/PartnerBrandPoints";
import PartnerBrandRewardsDataForm from "../../../../partnerBrand/PartnerBrandRewardsDataForm";
import {FormField} from "../../../../../validation/FormField";
import PartnerBrandRewardsData from "../../../../../model/partnerBrand/PartnerBrandRewardsData";
import PartnerBrandRewardsDataFormBody from "../../PartnerBrandRewardsDataFormBody";
import PartnerBrandBankDataForm from "../../../../partnerBrand/PartnerBrandBankDataForm";
import PartnerBrandBankDataFormBody from "../../PartnerBrandBankDataFormBody";
import PartnerBrandBankData from "../../../../../model/partnerBrand/PartnerBrandBankData";
import CustomField from "../../../../../model/customField/CustomField";
import CustomFieldValue from "../../../../../model/job/CustomFieldValue";
import PartnerBrandCustomFieldsValues from "../../PartnerBrandCustomFieldsValues";
import ButtonGroup from "../../../../../hiberUiKit/src/atoms/ButtonGroup/ButtonGroup";
import ButtonSecondary from "../../../../../hiberUiKit/src/atoms/Buttons/ButtonSecondary";
import {noop} from "react-select/lib/utils";
import {DealerPriceMatrixState} from "../../../../brand/dto/DealerPriceMatrixState";
import JobType from "../../../../../model/job/JobType";
import PartnerBrandJobTypesConfig from "../../PartnerBrandJobTypesConfig";
import {isDefined} from "../../../../../utils/Utils";

interface IComponentProps {
	/**
	 * On submit callback. Should call only when form is valid.
	 * @param {PartnerBrand} new brand object.
	 * @param {Option<Address>} selected address for partner brand.
	 * @param {Option<PartnerBrandRewardsData>} partner brand rewards data to be submitted.
	 * @param {Option<PartnerBrandBankData>} partner brand bank data to be submitted.
	 */
	onSubmit: (partnerBrand: PartnerBrand,
		address: Option<Address>,
		rewardsData: Option<PartnerBrandRewardsData>,
		bankData: Option<PartnerBrandBankData>,
		jobTypes: JobType[]) => void;
	/**
	 * On cancel button handler.
	 */
	onCancel: () => void;
	/**
	 * Indicate if any server action is in progress.
	 */
	isSubmitting: boolean;
	/**
	 * On add file
	 */
	onAddFile: (file: File) => any
	/**
	 * On remove file
	 */
	onRemoveFile: () => any
	/**
	 * On add points
	 * @param {PartnerBrandPoints} point
	 */
	onAddPoints: (point: PartnerBrandPoints) => void
	/**
	 * Newly added points
	 */
	points: PartnerBrandPoints[]
	/**
	 * If true, inputs related to partner brand rewards are displayed
	 */
	rewardFieldsAvailable: boolean;
	/**
	 * If true, bank data inputs are displayed
	 */
	bankFieldsAvailable: boolean;
	/**
	 * Suffix of default quote email style
	 */
	defaultQuoteEmailStyleSuffix: Option<string>;
	/**
	 * Partner Brand custom fields list.
	 */
	customFields: Option<CustomField[]>;
	/**
	 * Partner brand custom fields values
	 */
	customFieldsValues: Option<CustomFieldValue[]>;
	/**
	 * Update custom field values action.
	 * @param {CustomFieldValue[]} updatedCustomFieldsValues
	 */
	updateCustomFieldsValues: (updatedCustomFieldsValues: CustomFieldValue[]) => void;
	/**
	 * All globally available job types. At least one must be selected during creation.
	 */
	jobTypes: JobType[];
}

interface FormViewState {
	/**
	 * Additional form to handle reward related fields.
	 */
	rewardsDataForm: Option<PartnerBrandRewardsDataForm>;
	/**
	 * Additional form to handle bank data fields.
	 */
	bankDataForm: Option<PartnerBrandBankDataForm>;
	/**
	 * Editable job types configuration. Defaults to all inactive (none selected).
	 */
	jobTypes: JobType[];
	/**
	 * Validation error shown when the job types config is invalid on submit.
	 */
	jobTypesError: Option<string>;
}

type IState = BaseFormViewState<PartnerBrandForm> & FormViewState;

export default class CreatePartnerBrandFormView extends BaseFormView<PartnerBrandForm, IComponentProps, IState> {
	constructor(props: IComponentProps) {
		super(props);
		this.state = {
			form: PartnerBrandForm.createEmpty(this.props.defaultQuoteEmailStyleSuffix),
			rewardsDataForm: this.props.rewardFieldsAvailable ? option(PartnerBrandRewardsDataForm.createEmpty()) : none,
			bankDataForm: this.props.bankFieldsAvailable ? option(PartnerBrandBankDataForm.createEmpty()) : none,
			isFormDirty: false,
			shouldValidate: false,
			jobTypes: this.props.jobTypes.map(jobType => jobType.copy({active: false})),
			jobTypesError: none
		};
		this.onJobTypesChange = this.onJobTypesChange.bind(this);
		this.onDealerPriceMatrixChange = this.onDealerPriceMatrixChange.bind(this);
	}

	onDealerPriceMatrixChange(dealerPriceMatrix: DealerPriceMatrixState) {
		this.setState((prevState: IState) => ({form: prevState.form.copy({dealerPriceMatrix})}));
	}

	render() {
		return (
			<SectionView>
				<PartnerBrandFormBody form={this.state.form}
									  onFieldChange={this.onFieldChanged}
									  onFieldCompleted={this.onFieldCompleted}
									  onFocusChange={this.onFocusChange}
									  onFieldsChange={this.onFieldsChanged}
									  onDealerPriceMatrixChange={this.onDealerPriceMatrixChange}
									  partnerBrandId={none}
									  {...this.props}/>
				<PartnerBrandJobTypesConfig jobTypes={this.state.jobTypes}
											onChange={this.onJobTypesChange}
											error={this.state.jobTypesError.orNull}/>
				{this.props.customFields.getOrElse([]).length > 0 &&
				<PartnerBrandCustomFieldsValues customFields={this.props.customFields}
												customFieldsValues={this.props.customFieldsValues}
												updateCustomFieldsValues={this.props.updateCustomFieldsValues}/>}
				{this.state.bankDataForm.map(
					bankForm => <PartnerBrandBankDataFormBody form={bankForm}
															  onFieldsChange={this.onBankFieldsChanged}
															  onFieldChange={this.onBankFieldChanged}
															  {...this.props}/>).orNull}
				{this.state.rewardsDataForm.map(
					rewardsForm => <PartnerBrandRewardsDataFormBody partnerBrandId={none}
																	form={rewardsForm}
																	onFieldChange={this.onRewardFieldChanged}
																	{...this.props}
																	totalUsedPoints={0}
																	totalEarnedPoints={0}
																	totalAvailablePoints={0}
																	totalSubmittedPoints={0}/>).orNull}
				<FormButtonsView align={"right"}>
					<ButtonTertiary onClick={this.props.onCancel}
									label={"Cancel"}
									disabled={this.props.isSubmitting}/>
					<ButtonPrimary onClick={() => this.submitForm()}
								   label={"Create partner brand"}
								   disabled={this.props.isSubmitting}
								   isLoading={this.props.isSubmitting}/>
				</FormButtonsView>
			</SectionView>
		);
	}

	/**
	 * On change reward related field value.
	 * @param field updated field.
	 */
	onRewardFieldChanged = (field: FormField<any>) => {
		this.state.rewardsDataForm.map(rewardsForm => {
			const newForm = rewardsForm.updateField(field);
			this.setState({rewardsDataForm: option(this.state.shouldValidate ? newForm.validate() : newForm), isFormDirty: true});
		})
	};

	/**
	 * On change bank data fields value.
	 * @param fields updated fields.
	 */
	onBankFieldsChanged = (fields: FormField<any>[]) => {
		this.state.bankDataForm.map(bankForm => {
			const newForm = bankForm.updateFields(fields);
			this.setState({bankDataForm: option(this.state.shouldValidate ? newForm.validate() : newForm), isFormDirty: true});
		})
	};

	/**
	 * On change bank data field value.
	 * @param field
	 */
	onBankFieldChanged = (field: FormField<any>) => {
		this.state.bankDataForm.map(bankForm => {
			const newForm = bankForm.updateField(field);
			this.setState({bankDataForm: option(this.state.shouldValidate ? newForm.validate() : newForm), isFormDirty: true});
		})
	};

	/**
	 * On job types configuration change. Clears the validation error once the config becomes valid.
	 * @param jobTypes updated job types list.
	 */
	onJobTypesChange(jobTypes: JobType[]): void {
		this.setState({
			jobTypes,
			jobTypesError: this.state.jobTypesError.isDefined ? this.validateJobTypes(jobTypes) : none,
			isFormDirty: true
		});
	}

	/**
	 * Validate the job types configuration.
	 * @param jobTypes job types list.
	 * @return validation error message, or none when valid.
	 */
	validateJobTypes(jobTypes: JobType[]): Option<string> {
		const activeJobTypes = jobTypes.filter(jobType => jobType.active);
		if (!activeJobTypes.length) {
			return option("At least one job type must be selected");
		}
		if (activeJobTypes.some(jobType => !isDefined(jobType.fee))) {
			return option("A fee is required for every active job type");
		}
		return none;
	}

	submitForm(): void {
		const validatedBankDataForm = this.state.bankDataForm.map(bankForm => bankForm.validate());
		const validatedRewardsDataForm = this.state.rewardsDataForm.map(rewardsForm => rewardsForm.validate());
		const validatedMainForm = this.state.form.validate();
		const address: Option<Address> = this.state.form.address.getOptionalValue();

		const jobTypes: JobType[] = this.state.jobTypes;
		const jobTypesError = this.validateJobTypes(jobTypes);

		if (jobTypesError.isDefined) {
			this.setState({
				bankDataForm: validatedBankDataForm,
				rewardsDataForm: validatedRewardsDataForm,
				form: validatedMainForm,
				shouldValidate: true,
				jobTypesError
			});
			return;
		}

		if (validatedBankDataForm.isDefined && validatedRewardsDataForm.isDefined) {
			this.setState({
					bankDataForm: validatedBankDataForm,
					rewardsDataForm: validatedRewardsDataForm,
					form: validatedMainForm,
					shouldValidate: true,
					jobTypesError: none
				}, () =>
					validatedBankDataForm.get.onValid(bankData =>
						validatedRewardsDataForm.get.onValid(rewardsData =>
							validatedMainForm.onValid(partnerBrand =>
								this.props.onSubmit(partnerBrand, address, option(rewardsData), option(bankData), jobTypes))))
			);
		}
		else if (validatedBankDataForm.isDefined) {
			this.setState({bankDataForm: validatedBankDataForm, form: validatedMainForm, shouldValidate: true, jobTypesError: none}, () =>
				validatedBankDataForm.get.onValid(bankData =>
					validatedMainForm.onValid(partnerBrand =>
						this.props.onSubmit(partnerBrand, address, none, option(bankData), jobTypes)))
			);
		}
		else if (validatedRewardsDataForm.isDefined) {
			this.setState({rewardsDataForm: validatedRewardsDataForm, form: validatedMainForm, shouldValidate: true, jobTypesError: none}, () =>
				validatedRewardsDataForm.get.onValid(rewardsData =>
					validatedMainForm.onValid(partnerBrand =>
						this.props.onSubmit(partnerBrand, address, option(rewardsData), none, jobTypes)))
			);
		}
		else {
			this.setState({form: validatedMainForm, shouldValidate: true, jobTypesError: none}, () =>
				validatedMainForm.onValid(partnerBrand => this.props.onSubmit(partnerBrand, address, none, none, jobTypes)));
		}
	}
}
